/**
 * wing-hands.js — first-person goose wings on the controllers (GDD 3.3).
 *
 * Visible only in first-person stall mode. Each wing is parented to whatever
 * `input.getHand(side).object3D` currently is, which is the XR grip space in a
 * headset and a camera-relative node on the desktop — so the wings follow the
 * hands without this module knowing which is which.
 *
 * Poses react to grab state so the wing visibly closes around a ball.
 *
 * @module avatar/wing-hands
 */

import * as THREE from 'three';
import { input } from '../core/input.js';
import { grab } from '../core/grab.js';
import { createWingHand } from '../assets3d/factory.js';

class WingHands {
  constructor() {
    /** @type {{left: THREE.Group|null, right: THREE.Group|null}} */
    this.wings = { left: null, right: null };
    this.visible = false;
    /** @type {Record<string, THREE.Object3D|null>} Current parents. */
    this._attachedTo = { left: null, right: null };
    this._pose = { left: 'open', right: 'open' };
  }

  /** @param {THREE.Camera} camera Fallback parent before hands exist. */
  init(camera) {
    for (const side of ['left', 'right']) {
      const wing = createWingHand(side);
      wing.visible = false;
      this.wings[side] = wing;
      this._reparent(side);
    }
    return this;
  }

  /** @param {boolean} on */
  setVisible(on) {
    this.visible = on;
    for (const side of ['left', 'right']) {
      if (this.wings[side]) this.wings[side].visible = on;
    }
  }

  /**
   * @param {'left'|'right'} side
   * @returns {THREE.Group|null}
   */
  getWing(side) {
    return this.wings[side];
  }

  update(dt) {
    for (const side of ['left', 'right']) {
      this._reparent(side);
      if (!this.visible) continue;

      const wing = this.wings[side];
      if (!wing) continue;

      // Hide the wing on a hand with no tracked source, so a desktop player
      // does not see a phantom left wing floating beside them.
      const hand = input.getHand(side);
      wing.visible = this.visible && (input.isVR ? hand.active : side === input.activeSide);

      const holding = grab.isHolding(side);
      const pose = holding ? 'grip' : (hand.triggerPressed ? 'point' : 'open');
      if (pose !== this._pose[side]) {
        this._pose[side] = pose;
        wing.userData.setPose(pose);
      }
    }
  }

  /**
   * Keep each wing parented to its hand node, which swaps when a VR session
   * starts or ends.
   * @private
   */
  _reparent(side) {
    const wing = this.wings[side];
    if (!wing) return;
    const node = input.getHand(side)?.object3D;
    if (!node || this._attachedTo[side] === node) return;
    node.add(wing);
    this._attachedTo[side] = node;
  }
}

/** The one wing-hands system. */
export const wingHands = new WingHands();
